import type { SaleItem } from "../models/saleItem";
import { RISTA_FBT_PROVIDER } from "./constants";
import type { RecommendationsV1Payload, RecommendationsV2Payload } from "./types";

/**
 * `recommendations/request` — WHAT to ask the engine, and WHETHER to ask at all.
 *
 * Every function here is PURE: cart in, decision or payload out. No store, no dispatch, no
 * platform API. That is deliberate — these are the rules a second surface (kiosk) is most likely
 * to want to reuse or tune, and they are the rules most worth unit-testing.
 */

/** Values in exactly one of the two sets. */
export function symmetricDifference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const result = new Set(setA);
  for (const value of setB) {
    if (result.has(value)) result.delete(value);
    else result.add(value);
  }
  return result;
}

/**
 * Every distinct item id in the cart, ITEM OPTIONS INCLUDED.
 *
 * Options count because a cart of "Burger + extra cheese" should not get the same suggestions as
 * a plain burger — the engine is told about the modifiers too.
 */
export function getAllItemsAndOptionsIds(curSaleItems: SaleItem[] = []): string[] {
  const ids = new Set<string>();
  (curSaleItems || []).forEach((item) => {
    if (item?.itemId) {
      ids.add(item.itemId);
    }
    if (item?.itemOptionList?.length) {
      item.itemOptionList.forEach((itemOption: any) => {
        if (itemOption?.itemId) {
          ids.add(itemOption.itemId);
        }
      });
    }
  });
  return Array.from(ids);
}

/**
 * The item ids — and the item objects — that are completely new in the current cart, meaning
 * they did not appear at all in the previous one.
 *
 * The OBJECTS are returned as well as the ids because the V1 trigger has to look at
 * `recommendationInfo` on the added item: an item that was itself added FROM a recommendation
 * must not trigger a fresh recommendation, or the widget chases its own tail.
 */
export function getNewItemIds(
  prevSaleItems: SaleItem[] = [],
  curSaleItems: SaleItem[] = [],
): { addedItemIds: string[]; addedItems: SaleItem[] } {
  const prevIds = new Set((prevSaleItems || []).map((item) => item.itemId));
  const newIds = new Set<string>();
  const newItems = new Set<SaleItem>();

  for (const item of curSaleItems || []) {
    if (!prevIds.has(item.itemId)) {
      newIds.add(item.itemId);
      newItems.add(item);
    }
  }

  return {
    addedItemIds: Array.from(newIds),
    addedItems: Array.from(newItems),
  };
}

/**
 * Did the SET of item (and option) ids change between the two carts?
 *
 * A quantity change is NOT a cart change by this definition — bumping a burger from 1 to 2 does
 * not alter what would be recommended alongside it, so it must not burn an engine call.
 */
export function hasCartChanged(
  prevSaleItems: SaleItem[] = [],
  currentSaleItems: SaleItem[] = [],
): boolean {
  const prevSet = new Set<string>(getAllItemsAndOptionsIds(prevSaleItems));
  const currSet = new Set<string>(getAllItemsAndOptionsIds(currentSaleItems));
  return Boolean(symmetricDifference(prevSet, currSet).size);
}

/** The sale's own customer phone number, falling back to the customer store's, else `""`. */
export function resolveRecommendationPhoneNumber(saleData: any, customerStore: any): string {
  return (
    saleData?.customerInfo?.contactInfo?.phoneNumber ||
    customerStore?.customerInfo?.contactInfo?.phoneNumber ||
    ""
  );
}

/**
 * The V2 request body.
 *
 * Both fields are OMITTED rather than sent empty — an absent key and an empty one mean different
 * things to the engine, which is why this is a helper and not an object literal at the call site.
 */
export function buildRecommendationsV2Payload(
  saleData: any,
  customerStore: any,
): RecommendationsV2Payload {
  const phoneNumber = resolveRecommendationPhoneNumber(saleData, customerStore);
  const itemIds = getAllItemsAndOptionsIds(saleData?.saleItems || []);
  const dataToPost: RecommendationsV2Payload = {};

  if (phoneNumber) {
    dataToPost.phoneNumber = phoneNumber;
  }
  if (itemIds.length) {
    dataToPost.itemIds = itemIds;
  }

  return dataToPost;
}

/**
 * The V1 request body.
 *
 * `RISTA_FBT` ("frequently bought together") is asked about the WHOLE cart; every other V1
 * provider is asked about the single item that was just added.
 */
export function buildRecommendationsV1Payload(input: {
  provider?: string;
  selectedChannelData: any;
  addedItemIds: string[];
  allItemAndOptionsIds: string[];
}): RecommendationsV1Payload {
  const { provider, selectedChannelData, addedItemIds, allItemAndOptionsIds } = input;
  const payload: RecommendationsV1Payload = {
    channelDefId: selectedChannelData?.channelDefId,
    channelId: selectedChannelData?.channelId,
  };

  if (provider === RISTA_FBT_PROVIDER) {
    payload.itemIds = allItemAndOptionsIds;
  } else {
    payload.itemId = addedItemIds[0];
  }

  return payload;
}

/** What a cart change should make the flow do next. */
export type RecommendationCartAction =
  | { type: "NONE" }
  /** The cart emptied out — drop the suggestions on screen rather than leaving them stale. */
  | { type: "CLEAR" }
  | { type: "FETCH_V1"; payload: RecommendationsV1Payload }
  | { type: "FETCH_V2" };

/**
 * The V1 cart rule: should this cart change trigger a fetch, a clear, or nothing?
 *
 * Pure and total — the caller dispatches, this decides. Split out from the middleware so the
 * rule can be tested and reused without a store.
 */
export function decideV1CartAction(input: {
  recommenderConfig: { provider?: string; itemIdRequired?: boolean } | undefined;
  prevSaleItems: SaleItem[];
  currentSaleItems: SaleItem[];
  currentRecommendationId?: string;
  selectedChannelData: any;
}): RecommendationCartAction {
  const {
    recommenderConfig,
    prevSaleItems,
    currentSaleItems,
    currentRecommendationId,
    selectedChannelData,
  } = input;

  // The account is not configured to be asked by item id — V1 has nothing to send.
  if (!recommenderConfig?.itemIdRequired) {
    return { type: "NONE" };
  }

  if (!currentSaleItems.length && currentRecommendationId) {
    return { type: "CLEAR" };
  }

  const { addedItemIds, addedItems } = getNewItemIds(prevSaleItems, currentSaleItems);
  const provider = recommenderConfig.provider;

  const shouldFetch =
    provider === RISTA_FBT_PROVIDER
      ? hasCartChanged(prevSaleItems, currentSaleItems)
      : // Not a re-trigger from an item that was itself recommended.
        Boolean(addedItemIds.length && !addedItems[0]?.recommendationInfo);

  if (!shouldFetch) {
    return { type: "NONE" };
  }

  return {
    type: "FETCH_V1",
    payload: buildRecommendationsV1Payload({
      provider,
      selectedChannelData,
      addedItemIds,
      allItemAndOptionsIds: getAllItemsAndOptionsIds(currentSaleItems),
    }),
  };
}

/**
 * The V2 cart rule.
 *
 * V2 re-queries on either input changing: the set of items, or the customer.
 *
 * ⚠️ The phone numbers compared are the SALE's, on both sides — never the customer store's.
 * `customerStore` is often updated earlier in the same interaction (the delivery/dine-in modal
 * writes it before the sale), so resolving with the store fallback makes prev === current and
 * silently skips the fetch that customer change was supposed to cause.
 */
export function shouldFetchV2OnCartChange(input: {
  prevSaleData: any;
  currentSaleData: any;
}): boolean {
  const { prevSaleData, currentSaleData } = input;

  const prevSalePhone = prevSaleData?.customerInfo?.contactInfo?.phoneNumber || "";
  const currentSalePhone = currentSaleData?.customerInfo?.contactInfo?.phoneNumber || "";
  const phoneChanged = prevSalePhone !== currentSalePhone;
  const itemIdSetChanged = hasCartChanged(
    prevSaleData?.saleItems || [],
    currentSaleData?.saleItems || [],
  );

  return phoneChanged || itemIdSetChanged;
}
